import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Pencil, Plus, Save, Send, Table2, Trash2, X } from "lucide-react";
import { useState } from "react";

import {
  actualizarVacacion,
  borrarVacacion,
  crearVacacion,
  descargarFormatoVacacion,
  enviarFormatoVacacion,
  getDiasFestivos,
  getSaldo,
  getVacaciones,
  type Vacacion,
  type VacacionInput,
} from "@/lib/api";
import { DescargandoOverlay } from "@/components/DescargandoOverlay";
import { currentEmail } from "@/lib/auth";
import { diasHabiles, fechaLimiteSolicitud, hoyISO, TIPOS, tipoColor, tipoLabel } from "@/lib/tipos";

function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const BADGE: Record<string, [string, string]> = {
  pendiente: ["Pendiente", "bg-amber-100 text-amber-700"],
  autorizado: ["Autorizado", "bg-emerald-100 text-emerald-700"],
  rechazado: ["Rechazado", "bg-red-100 text-red-700"],
  baja_pendiente: ["Baja pendiente", "bg-orange-100 text-orange-700"],
};

export function MisVacacionesPage() {
  const qc = useQueryClient();
  const myEmail = currentEmail() ?? "";

  const q = useQuery({
    queryKey: ["vacaciones", "mias", myEmail],
    queryFn: () => getVacaciones({ empleado: myEmail }),
    enabled: !!myEmail,
  });
  const saldoQ = useQuery({ queryKey: ["saldo"], queryFn: getSaldo });
  const festivosQ = useQuery({ queryKey: ["dias-festivos"], queryFn: getDiasFestivos, staleTime: Infinity });
  const festivos = new Set((festivosQ.data ?? []).map((f) => f.fecha));

  const [festivosOpen, setFestivosOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [tipo, setTipo] = useState<string>(TIPOS[0].value);
  const [comentario, setComentario] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const reset = () => {
    setEditingId(null);
    setFechaInicio("");
    setFechaFin("");
    setTipo(TIPOS[0].value);
    setComentario("");
    setError(null);
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["vacaciones"] });
    qc.invalidateQueries({ queryKey: ["saldo"] });
  };

  const crear = useMutation({
    mutationFn: (v: VacacionInput) => crearVacacion(v),
    onSuccess: (vac) => {
      refresh();
      reset();
      setMsg(
        vac.estatus === "autorizado"
          ? "Vacaciones registradas y autorizadas."
          : "Solicitud enviada — pendiente de autorización de tu jefe.",
      );
    },
    onError: (e: Error) => setError(e.message),
  });
  const actualizar = useMutation({
    mutationFn: ({ id, v }: { id: number; v: VacacionInput }) => actualizarVacacion(id, v),
    onSuccess: () => {
      refresh();
      reset();
    },
    onError: (e: Error) => setError(e.message),
  });
  const borrar = useMutation({
    mutationFn: (id: number) => borrarVacacion(id),
    onSuccess: (res) => {
      refresh();
      setMsg(
        res.resultado === "baja_solicitada"
          ? "Baja solicitada — pendiente de autorización de tu jefe."
          : "Registro eliminado.",
      );
    },
    onError: (e: Error) => setError(e.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    if (!fechaInicio || !fechaFin) {
      setError("Captura fecha de inicio y fin.");
      return;
    }
    if (fechaFin < fechaInicio) {
      setError("La fecha fin no puede ser anterior al inicio.");
      return;
    }
    const v: VacacionInput = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      tipo,
      comentario: comentario.trim() || null,
    };
    if (editingId != null) actualizar.mutate({ id: editingId, v });
    else crear.mutate(v);
  };

  const startEdit = (row: Vacacion) => {
    setEditingId(row.id);
    setFechaInicio(row.fecha_inicio);
    setFechaFin(row.fecha_fin);
    setTipo(row.tipo);
    setComentario(row.comentario ?? "");
    setError(null);
    setMsg(null);
  };

  const generarFormato = async (row: Vacacion) => {
    if (descargando) return;
    setError(null);
    setMsg(null);
    setDescargando(true);
    try {
      await descargarFormatoVacacion(row.id, row.fecha_inicio);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  const enviarFormato = async (row: Vacacion) => {
    if (enviando || descargando) return;
    if (
      !confirm(
        `¿Enviar por correo a Recursos Humanos el formato PDF de tus vacaciones del ${fmtFecha(row.fecha_inicio)} al ${fmtFecha(row.fecha_fin)}?`,
      )
    )
      return;
    setError(null);
    setMsg(null);
    setEnviando(true);
    try {
      const res = await enviarFormatoVacacion(row.id);
      setMsg(
        res.resultado === "omitido_dev"
          ? "En desarrollo no se envían correos."
          : `Formato enviado a ${res.destinatario ?? "RH"}.`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  const onDelete = (row: Vacacion) => {
    const texto =
      row.estatus === "autorizado"
        ? "Estas vacaciones están autorizadas. Se enviará una solicitud de BAJA a tu jefe. ¿Continuar?"
        : "¿Borrar este periodo?";
    if (confirm(texto)) borrar.mutate(row.id);
  };

  const saving = crear.isPending || actualizar.isPending;
  const rows = q.data ?? [];
  const s = saldoQ.data;
  const bolsa = s?.bolsa ?? 0;
  const usados = s?.usados ?? 0;
  const saldo = s?.saldo ?? 0;
  const pendientes = s?.pendientes ?? 0;

  return (
    <div className="space-y-5 max-w-4xl">
      <DescargandoOverlay
        visible={descargando || enviando}
        titulo={enviando ? "Enviando…" : "Descargando…"}
        detalle={
          enviando
            ? "Estamos generando el formato en PDF y enviándolo por correo a RH, espera un momento."
            : "Estamos generando tu formato de vacaciones en PDF, espera un momento."
        }
      />
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Mis vacaciones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Captura tus periodos. Si tienes jefe, quedan pendientes de su autorización.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
        <div className="bg-white border rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-slate-800 tabular-nums">{bolsa}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">
            Asignados{s?.anio_antiguedad ? ` (año ${s.anio_antiguedad})` : ""}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-blue-600 tabular-nums">{usados}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Usados</div>
        </div>
        <div className="bg-white border rounded-lg p-3 text-center">
          <div className={`text-2xl font-semibold tabular-nums ${saldo < 0 ? "text-red-600" : "text-emerald-600"}`}>
            {saldo}
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Bolsa</div>
        </div>
        <div
          className="bg-white border rounded-lg p-3 text-center"
          title={
            s?.pendientes_vence
              ? `Días que te quedan de la bolsa anterior. Vencen el ${fmtFecha(s.pendientes_vence)}.`
              : "Solo hay una bolsa vigente, así que no hay días de una bolsa anterior por vencer."
          }
        >
          <div className={`text-2xl font-semibold tabular-nums ${pendientes > 0 ? "text-amber-600" : "text-slate-300"}`}>
            {pendientes}
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Pendientes</div>
          {s?.pendientes_vence && (
            <div className="text-[10px] text-amber-600 mt-0.5 tabular-nums">
              vencen {fmtFecha(s.pendientes_vence)}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDetalleOpen(true)}
        disabled={!s?.detalle?.length}
        className="-mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600
                   border rounded-md px-2.5 py-1.5 bg-white hover:bg-slate-50
                   disabled:opacity-40 disabled:cursor-not-allowed w-fit"
        title="Ver cada abono con su vencimiento"
      >
        <Table2 className="w-3.5 h-3.5" /> Ver detalle de la bolsa
      </button>

      <p className="text-[11px] text-slate-400 -mt-2">
        Días hábiles (L–V, sin{" "}
        <button
          type="button"
          onClick={() => setFestivosOpen(true)}
          className="underline decoration-dotted underline-offset-2 hover:text-slate-600"
          title="Ver los días festivos considerados"
        >
          festivos
        </button>
        ). Solo el tipo “Vacaciones” descuenta.
        {s && !s.fecha_ingreso && " · Sin fecha de ingreso en el directorio (bolsa = 0)."}
        {s?.proximo_abono && s.proximo_abono_dias != null && (
          <>
            {" · "}Tu bolsa suma{" "}
            <b className="text-slate-600">+{s.proximo_abono_dias} días</b> el{" "}
            <b className="text-slate-600">
              {new Date(`${s.proximo_abono}T00:00:00`).toLocaleDateString("es-MX", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </b>{" "}
            (tu aniversario).
          </>
        )}
      </p>

      <form onSubmit={submit} className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Desde</span>
          <input type="date" className="mt-1 border rounded px-2 py-1.5 text-sm block"
            value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          {fechaInicio && (() => {
            const limite = fechaLimiteSolicitud(fechaInicio);
            const tarde = hoyISO() > limite;
            return (
              <span className={`block mt-1 text-[11px] ${tarde ? "text-red-600 font-medium" : "text-slate-400"}`}
                title="Política: la solicitud se hace en la quincena anterior a la del goce">
                {tarde
                  ? `Fuera de política: debía solicitarse a más tardar el ${fmtFecha(limite)}`
                  : `Solicitar a más tardar el ${fmtFecha(limite)}`}
              </span>
            );
          })()}
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Hasta</span>
          <input type="date" className="mt-1 border rounded px-2 py-1.5 text-sm block"
            value={fechaFin} min={fechaInicio || undefined} onChange={(e) => setFechaFin(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Tipo</span>
          <select className="mt-1 border rounded px-2 py-1.5 text-sm block w-40"
            value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="block flex-1 min-w-[12rem]">
          <span className="text-xs font-medium text-slate-600">Comentario (opcional)</span>
          <input type="text" className="mt-1 border rounded px-2 py-1.5 text-sm block w-full"
            value={comentario} maxLength={200} onChange={(e) => setComentario(e.target.value)} />
        </label>
        <div className="flex items-center gap-2">
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50">
            {editingId != null ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editingId != null ? (saving ? "Guardando…" : "Guardar") : saving ? "Enviando…" : "Solicitar"}
          </button>
          {editingId != null && (
            <button type="button" onClick={reset}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
              <X className="w-4 h-4" /> Cancelar
            </button>
          )}
        </div>
      </form>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>}
      {msg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">{msg}</div>}

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Periodo</th>
              <th className="px-3 py-2 font-medium text-right">Días háb.</th>
              <th className="px-3 py-2 font-medium">Tipo</th>
              <th className="px-3 py-2 font-medium">Estatus</th>
              <th className="px-3 py-2 font-medium">Comentario</th>
              <th className="px-3 py-2 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Cargando…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Aún no tienes periodos capturados.</td></tr>
            )}
            {rows.map((r) => {
              const [label, cls] = BADGE[r.estatus] ?? [r.estatus, "bg-slate-100 text-slate-600"];
              return (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 tabular-nums">
                    {fmtFecha(r.fecha_inicio)} – {fmtFecha(r.fecha_fin)}
                    {r.fuera_politica && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 align-middle"
                        title="Se capturó después del cierre de la quincena anterior al goce">
                        Fuera de política
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{diasHabiles(r.fecha_inicio, r.fecha_fin, festivos)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tipoColor(r.tipo) }} />
                      {tipoLabel(r.tipo)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.comentario ?? "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" onClick={() => generarFormato(r)} disabled={descargando || enviando}
                      className="text-slate-500 hover:text-emerald-600 p-1 disabled:opacity-50"
                      title="Descargar formato (PDF)">
                      <FileSpreadsheet className="w-4 h-4" />
                    </button>
                    {r.estatus === "autorizado" && (
                      <button type="button" onClick={() => enviarFormato(r)} disabled={descargando || enviando}
                        className="text-slate-500 hover:text-sky-600 p-1 disabled:opacity-50"
                        title="Enviar el formato (PDF) por correo a RH">
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {r.estatus === "pendiente" && (
                      <button type="button" onClick={() => startEdit(r)}
                        className="text-slate-500 hover:text-blue-600 p-1" title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {r.estatus !== "baja_pendiente" && (
                      <button type="button" onClick={() => onDelete(r)}
                        className="text-slate-500 hover:text-red-600 p-1"
                        title={r.estatus === "autorizado" ? "Solicitar baja" : "Borrar"}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detalleOpen && s && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetalleOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold">Detalle de la bolsa</h2>
              <button
                type="button"
                onClick={() => setDetalleOpen(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto px-5 py-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                    <th className="text-left font-semibold py-1.5">Asignación</th>
                    <th className="text-right font-semibold py-1.5">Asignadas</th>
                    <th className="text-right font-semibold py-1.5">Usadas</th>
                    <th className="text-right font-semibold py-1.5">Restantes</th>
                    <th className="text-right font-semibold py-1.5">Vencidas</th>
                    <th className="text-left font-semibold py-1.5 pl-4">Vence</th>
                  </tr>
                </thead>
                <tbody>
                  {s.detalle.map((b) => (
                    <tr
                      key={b.asignada}
                      className={`border-b last:border-0 ${b.vigente ? "" : "text-slate-400"}`}
                    >
                      <td className="py-1.5 tabular-nums">{fmtFecha(b.asignada)}</td>
                      <td className="py-1.5 text-right tabular-nums">{b.dias}</td>
                      <td className="py-1.5 text-right tabular-nums">{b.usados}</td>
                      <td
                        className={`py-1.5 text-right tabular-nums ${
                          b.vigente ? "font-semibold text-emerald-600" : ""
                        }`}
                      >
                        {b.restantes}
                      </td>
                      <td
                        className={`py-1.5 text-right tabular-nums ${
                          b.vencidas > 0 ? "font-semibold text-red-600" : ""
                        }`}
                      >
                        {b.vencidas}
                      </td>
                      <td className="py-1.5 pl-4 tabular-nums">
                        {fmtFecha(b.vence)}
                        {!b.vigente && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide">vencida</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="py-2">Total</td>
                    <td className="py-2 text-right tabular-nums">
                      {s.detalle.reduce((a, b) => a + b.dias, 0)}
                    </td>
                    <td className="py-2 text-right tabular-nums">{s.usados_totales}</td>
                    <td className="py-2 text-right tabular-nums text-emerald-600">{s.saldo}</td>
                    <td className="py-2 text-right tabular-nums text-red-600">
                      {s.vencidas_totales}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="px-5 py-2.5 border-t text-[11px] text-slate-500">
              Cada abono entra en tu aniversario y vence 18 meses después, así que
              siempre hay medio año con dos bolsas vivas. Los periodos consumen primero
              la bolsa más antigua que siga vigente; lo que no alcanzaste a usar antes
              del vencimiento se pierde y aparece en “Vencidas”.
            </div>
          </div>
        </div>
      )}

      {festivosOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setFestivosOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold">Días festivos considerados</h2>
              <button
                type="button"
                onClick={() => setFestivosOpen(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto px-5 py-3">
              {(festivosQ.data ?? []).length === 0 && (
                <p className="text-sm text-slate-500 py-4 text-center">Sin días festivos cargados.</p>
              )}
              {[...new Set((festivosQ.data ?? []).map((f) => f.fecha.slice(0, 4)))].map((anio) => (
                <div key={anio} className="mb-3">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    {anio}
                  </div>
                  <ul className="space-y-0.5">
                    {(festivosQ.data ?? [])
                      .filter((f) => f.fecha.startsWith(anio))
                      .map((f) => (
                        <li key={f.fecha} className="text-sm flex gap-3">
                          <span className="tabular-nums text-slate-600 w-24 shrink-0">
                            {fmtFecha(f.fecha)}
                          </span>
                          <span className="text-slate-800">{f.descripcion}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="px-5 py-2.5 border-t text-[11px] text-slate-500">
              Descanso obligatorio (LFT Art. 74). Estos días no cuentan como hábiles
              al calcular las vacaciones.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
